import { API_ORIGIN } from '../config'
const API_BASE = `${API_ORIGIN}/api`

export async function getTechnicians(parkId) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/technicians`)
  if (!response.ok) throw new Error('Error al obtener los técnicos')
  return response.json()
}

export async function getTicketCounts(parkId) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/tickets/counts`)
  if (!response.ok) throw new Error('Error al obtener los contadores de tickets')
  return response.json()
}

export async function searchTickets(parkId, query) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/tickets/search?q=${encodeURIComponent(query)}`)
  if (!response.ok) throw new Error('Error al buscar tickets')
  return response.json()
}

export async function getMaintenanceMetrics(parkId) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/maintenance/metrics`)
  if (!response.ok) throw new Error('Error al obtener las métricas de mantenimiento')
  return response.json()
}

export async function getKanbanBoard(parkId, filters = {}) {
  const params = new URLSearchParams()
  if (filters.search) params.set('search', filters.search)
  if (filters.types?.length) params.set('types', filters.types.join(','))
  if (filters.technicianIds?.length) params.set('technicianIds', filters.technicianIds.join(','))
  if (filters.resolvedPeriodDays) params.set('resolvedPeriodDays', filters.resolvedPeriodDays)

  const response = await fetch(`${API_BASE}/parks/${parkId}/tickets/board?${params.toString()}`)
  if (!response.ok) throw new Error('Error al obtener el tablero')
  return response.json()
}

export async function updateTicketStatus(ticketId, payload) {
  const response = await fetch(`${API_BASE}/tickets/${ticketId}/status`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || 'Error al actualizar el estatus')
  }
  return response.json()
}

export async function assignTechnician(ticketId, technicianId) {
  const response = await fetch(`${API_BASE}/tickets/${ticketId}/technician`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ technicianId }),
  })
  if (!response.ok) throw new Error('Error al asignar el técnico')
  return response.json()
}

// Acciones del menú «···» del ticket. Los errores traen el mensaje legible del servidor.
async function ticketAction(path, options, fallback) {
  const response = await fetch(`${API_BASE}/tickets/${path}`, options)
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.message || fallback)
  }
  return response.json()
}
const jsonPost = (body) => ({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })

export const escalateTicket = (ticketId, reason) => ticketAction(`${ticketId}/escalate`, jsonPost({ reason }), 'No se pudo escalar el ticket')
export const duplicateTicket = (ticketId) => ticketAction(`${ticketId}/duplicate`, jsonPost({}), 'No se pudo duplicar el ticket')
export const getTicketLinkCandidates = (ticketId, query) => ticketAction(`${ticketId}/link-candidates?q=${encodeURIComponent(query || '')}`, {}, 'No se pudo buscar tickets')
export const linkTicket = (ticketId, otherTicketId) => ticketAction(`${ticketId}/links`, jsonPost({ otherTicketId }), 'No se pudo vincular el ticket')
export const unlinkTicket = (ticketId, otherTicketId) => ticketAction(`${ticketId}/links/${otherTicketId}`, { method: 'DELETE' }, 'No se pudo quitar el vínculo')

export async function archiveTicket(ticketId, reason) {
  const response = await fetch(`${API_BASE}/tickets/${ticketId}/archive`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }),
  })
  if (!response.ok) throw new Error('Error al archivar el ticket')
  return response.json()
}

export async function getTicketsList(parkId, filters = {}) {
  const params = new URLSearchParams()
  if (filters.search) params.set('search', filters.search)
  if (filters.types?.length) params.set('types', filters.types.join(','))
  if (filters.technicianIds?.length) params.set('technicianIds', filters.technicianIds.join(','))

  const response = await fetch(`${API_BASE}/parks/${parkId}/tickets/list?${params.toString()}`)
  if (!response.ok) throw new Error('Error al obtener la lista de tickets')
  return response.json()
}

export async function getPlotTicketHistory(plotId) {
  const response = await fetch(`${API_BASE}/parks/plots/${plotId}/ticket-history`)
  if (!response.ok) throw new Error('Error al obtener el historial de la nave')
  return response.json()
}

export async function getTicketDetail(ticketId) {
  const response = await fetch(`${API_BASE}/tickets/${ticketId}/detail`)
  if (!response.ok) throw new Error('Error al obtener el detalle del ticket')
  return response.json()
}

export async function updateTicketPriority(ticketId, priority) {
  const response = await fetch(`${API_BASE}/tickets/${ticketId}/priority`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ priority }),
  })
  if (!response.ok) throw new Error('Error al actualizar la prioridad')
  return response.json()
}

export async function sendTicketMessage(ticketId, { text, isInternal, file }) {
  const formData = new FormData()
  formData.append('text', text)
  formData.append('isInternal', isInternal)
  formData.append('author', 'Administrador')
  formData.append('authorRole', 'ADMIN')
  if (file) formData.append('file', file)

  const response = await fetch(`${API_BASE}/tickets/${ticketId}/messages`, { method: 'POST', body: formData })
  if (!response.ok) throw new Error('Error al enviar el mensaje')
  return response.json()
}

export async function addCostLine(ticketId, payload) {
  const response = await fetch(`${API_BASE}/tickets/${ticketId}/costs`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  })
  if (!response.ok) throw new Error('Error al agregar el costo')
  return response.json()
}

export async function updateCostLine(costId, payload) {
  const response = await fetch(`${API_BASE}/tickets/costs/${costId}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.message || 'Error al actualizar el costo')
  }
  return response.json()
}

// Elimina la línea de costo y, si estaba marcada para cobro y sin pagos, también el cobro al inquilino.
// Con keepPaidAsCredit, lo que el inquilino ya había pagado de ese cobro queda como saldo a favor.
export async function deleteCostLine(costId, { keepPaidAsCredit = false } = {}) {
  const response = await fetch(`${API_BASE}/tickets/costs/${costId}${keepPaidAsCredit ? '?keepPaidAsCredit=1' : ''}`, { method: 'DELETE' })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.message || 'Error al eliminar el costo')
  }
  return response.json()
}

export async function uploadTicketPhoto(ticketId, file) {
  const formData = new FormData()
  formData.append('file', file)
  const response = await fetch(`${API_BASE}/tickets/${ticketId}/photos`, { method: 'POST', body: formData })
  if (!response.ok) throw new Error('Error al subir la foto')
  return response.json()
}

export function photoUrl(filePath) {
  return `${API_ORIGIN}/uploads/${filePath}`
}

export async function searchPlotsForTicket(parkId, query) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/plots/search-for-ticket?q=${encodeURIComponent(query)}`)
  if (!response.ok) throw new Error('Error al buscar naves')
  return response.json()
}

export async function getTechnicianOptions(parkId) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/technicians/options`)
  if (!response.ok) throw new Error('Error al obtener técnicos')
  return response.json()
}

export async function getScheduledMaintenanceOptions(parkId, plotId) {
  const qs = plotId ? `?plotId=${encodeURIComponent(plotId)}` : ''
  const response = await fetch(`${API_BASE}/parks/${parkId}/scheduled-maintenance/options${qs}`)
  if (!response.ok) throw new Error('Error al obtener los mantenimientos programados')
  return response.json()
}

export async function createTicket(payload) {
  const response = await fetch(`${API_BASE}/tickets`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || 'Error al crear el ticket')
  }
  return response.json()
}

export async function getTechniciansForManager(parkId) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/technicians/manager`)
  if (!response.ok) throw new Error('Error al obtener los técnicos')
  return response.json()
}

export async function createTechnician(payload) {
  const response = await fetch(`${API_BASE}/technicians`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  })
  if (!response.ok) throw new Error('Error al crear el técnico')
  return response.json()
}

export async function updateTechnician(technicianId, payload) {
  const response = await fetch(`${API_BASE}/technicians/${technicianId}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  })
  if (!response.ok) throw new Error('Error al actualizar el técnico')
  return response.json()
}

export async function getWhatsAppStatus(parkId) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/config/whatsapp/status`)
  if (!response.ok) throw new Error('Error al obtener el estatus')
  return response.json()
}
// ===== Mantenimiento preventivo (calendario) =====

export async function getParkZones(parkId) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/zones-list`)
  if (!response.ok) throw new Error('Error al obtener las zonas')
  return response.json()
}

export async function getScheduledMaintenanceMonth(parkId, year, month) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/scheduled-maintenance/month?year=${year}&month=${month}`)
  if (!response.ok) throw new Error('Error al obtener el calendario de mantenimiento preventivo')
  return response.json()
}

export async function getScheduledMaintenanceDetail(scheduledId) {
  const response = await fetch(`${API_BASE}/scheduled-maintenance/${scheduledId}`)
  if (!response.ok) throw new Error('Error al obtener el detalle')
  return response.json()
}

export async function createScheduledMaintenance(parkId, payload) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/scheduled-maintenance`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || 'Error al programar el mantenimiento')
  }
  return response.json()
}

export async function updateScheduledMaintenance(scheduledId, payload) {
  const response = await fetch(`${API_BASE}/scheduled-maintenance/${scheduledId}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  })
  if (!response.ok) throw new Error('Error al actualizar el mantenimiento')
  return response.json()
}

export async function cancelScheduledMaintenance(scheduledId, reason) {
  const response = await fetch(`${API_BASE}/scheduled-maintenance/${scheduledId}/cancel`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }),
  })
  if (!response.ok) throw new Error('Error al cancelar el mantenimiento')
  return response.json()
}

export async function completeScheduledMaintenance(scheduledId) {
  const response = await fetch(`${API_BASE}/scheduled-maintenance/${scheduledId}/complete`, { method: 'POST' })
  if (!response.ok) throw new Error('Error al marcar como completado')
  return response.json()
}

export async function generateTicketFromScheduledMaintenance(scheduledId) {
  const response = await fetch(`${API_BASE}/scheduled-maintenance/${scheduledId}/generate-ticket`, { method: 'POST' })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || 'Error al generar el ticket')
  }
  return response.json()
}

export async function deleteTicketPhoto(photoId) {
  const response = await fetch(`${API_BASE}/tickets/photos/${photoId}`, { method: 'DELETE' })
  if (!response.ok) throw new Error('Error al eliminar la foto')
  return response.json()
}